import type { MutableRefObject } from "react";
import type { FroalaEditorInstance } from "./types";

const rangeBelongsToRoot = (root: HTMLElement, range: Range): boolean =>
  range.commonAncestorContainer === root ||
  root.contains(range.commonAncestorContainer);

export const getEditorOwnedRange = (
  root: HTMLElement,
  selection: Selection | null,
  savedRange: Range | null,
): Range | null => {
  if (selection && selection.rangeCount > 0) {
    const current = selection.getRangeAt(0);
    if (rangeBelongsToRoot(root, current)) return current;
  }
  return savedRange && rangeBelongsToRoot(root, savedRange) ? savedRange : null;
};

export const updateSelection = (
  froalaInstance: MutableRefObject<FroalaEditorInstance | null>,
  lastRange: MutableRefObject<Range | null>,
  selectedText: MutableRefObject<string | null>,
) => {
  const sel = document.getSelection();
  if (sel && sel.rangeCount > 0) {
    const r = sel.getRangeAt(0).cloneRange();
    const root = froalaInstance.current?.$el?.get(0);
    if (root && !rangeBelongsToRoot(root, r)) {
      return;
    }
    selectedText.current = froalaInstance.current?.selection?.text() ?? null;
    if (r) {
      lastRange.current = r;

      let el: HTMLElement | Node | null = r.commonAncestorContainer;
      if (!(el instanceof HTMLElement)) el = el.parentElement;

      if (el instanceof HTMLElement) {
        if (el.getAttribute("data-rkpi-active")) {
          const nRange = new Range();
          nRange.selectNode(el);
          sel.removeAllRanges();
          sel.addRange(nRange);
          lastRange.current = nRange;
        }
      }
    }
  }
};

/**
 * Restores the lastRange into the current selection, handling BR edge cases.
 * Used by multiple insert handlers (dynamic variable, icon, emoji, rkpi).
 */
export const restoreLastRange = (
  lastRange: MutableRefObject<Range | null>,
  sel: Selection,
): Range | null => {
  let range = lastRange.current;

  if (!range) {
    if (sel.rangeCount === 0) return null;
    range = sel.getRangeAt(0);
    if (!range) return null;
  }

  if (range.startContainer.nodeName === "BR") {
    const parent = range.startContainer.parentElement;
    if (!parent) return null;
    range = new Range();
    range.setStart(parent, 0);
  }

  sel.removeAllRanges();
  sel.addRange(range);
  return range;
};
